const STORAGE_KEY = "__app_state__";
const MAX_SIZE = 4_500_000; // ~4.5MB safe limit

export function loadState<T = any>(): T | undefined {
  if (typeof window === "undefined") return undefined; // SSR safe
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    return JSON.parse(raw) as T;
  } catch (err) {
    // Corrupted or too-large payload — auto heal
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore errors during cleanup
    }
    return undefined;
  }
}

export function saveState<T = any>(state: T) {
  if (typeof window === "undefined") return; // SSR safe
  try {
    const payload = JSON.stringify(state);
    // Prevent quota crash
    if (payload.length > MAX_SIZE) {
      console.warn("Persisted state too large, skipping save");
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, payload);
  } catch (err) {
    // Quota / private mode / blocked storage
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore errors during cleanup
    }
  }
}

export function throttle<T extends (...args: any[]) => void>(
  fn: T,
  ms: number
) {
  let last = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    const now = Date.now();
    const remain = ms - (now - last);

    if (remain <= 0) {
      last = now;
      fn(...args);
    } else if (!timer) {
      timer = setTimeout(() => {
        last = Date.now();
        timer = null;
        fn(...args);
      }, remain);
    }
  };
}
